<template>
	<li
		:id="`location-card-${location.mmsClubId}`"
		class="lifeclinic-card result-card border-bottom mx-n2 mx-md-0 p-2 d-md-flex"
		:class="{ selected: isSelected }"
		tabindex="0"
		:data-testid="`location-card-${location.mmsClubId}`"
	>
		<picture v-if="xsImage && smImage" class="position-relative">
			<source
				media="(min-width:992px) and (max-width: 1300px)"
				data-testid="thumbnail-xs"
				:srcset="xsImage.path"
			/>
			<img
				:alt="smImage.alt ?? `${location.clubTitle} Exterior`"
				data-testid="thumbnail-sm"
				:src="smImage.path"
				class="result-image rounded-3 mb-1 mb-md-0"
			/>
		</picture>

		<div class="result-body d-flex flex-column justify-content-start flex-grow-1">
			<div>
				<div class="result-title h3 mb-1">{{ location.clubTitle }}</div>
				<div class="result-address small mb-2" data-testid="location-info">
					{{ location.street1 }}, {{ location.city }}, {{ location.stateAbbr }} {{ location.zip }}
					<br />
					<span v-if="isClosed && phone">For questions call:</span>
					<a v-if="phone" :href="`tel:${phone.replace(/\D/g, '')}`">{{ phone }}</a>
				</div>
			</div>

			<div class="result-details d-flex justify-content-between align-items-center h-100">
				<div class="temp-closed-container d-flex align-items-center min-w-0">
					<div v-if="isClosed" class="me-1 min-w-0" data-testid="closed">
						<span class="badge rounded-pill rounded-pill-sm border">Temporarily Closed</span>
					</div>
				</div>
				<a
					v-if="location.locationPagePath"
					:href="location.locationPagePath"
					class="btn btn-secondary btn-sm py-1 px-2 rounded-3 align-self-md-end"
					data-testid="details-link"
					v-adobe-data-layer="detailsLinkADLConfig"
				>
					Details<span class="visually-hidden"> about {{ location.clubTitle }}</span>
				</a>
			</div>
		</div>
	</li>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import vAdobeDataLayer from '@/directives/AdobeDataLayer';
import { useMapStore } from '@/stores/map';
import { findImage } from '@/util/location';
import type { Location } from 'Types/aemui/find-a-location/findaLocation';

const props = defineProps<{
	location: Location;
	isSelected: boolean;
	isMobileMapView: boolean;
}>();

const mapStore = useMapStore();
const { defaultPhone } = storeToRefs(mapStore);

const xsImage = computed(() => findImage(props.location, '1x1'));
const smImage = computed(() => findImage(props.location, '16x9'));
const clubPhone = computed(() => props.location.phone);

const isClosed = computed(() => {
	return props.location.d360FilterItems?.Lifeclinic73?.closedLocation ?? false;
});

const phone = computed(() => {
	if (isClosed.value && defaultPhone.value) {
		return defaultPhone.value;
	} else if (props.location.d360FilterItems?.Lifeclinic73?.phoneNumber) {
		return props.location.d360FilterItems?.Lifeclinic73?.phoneNumber;
	} else if (clubPhone.value) {
		return clubPhone.value;
	} else {
		return '';
	}
});

const detailsLinkADLConfig = {
	name: 'findLocationLifeClinic',
	type: 'link',
	title: 'details link',
	link: props.location.locationPagePath
};
</script>
